import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClientData } from "../../redux/reducers/user/slice";
import { fetchUserInfo } from "../../redux/reducers/trello/slice";
import {
  Paper,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Box,
} from "@mui/material";
import styles from "./instructionsPage.module.css";

function InstructionsPage() {
  const dispatch = useDispatch();
  const docRef = useRef(null);

  const { isClientFetched } = useSelector((state) => state.user);
  const { email, getUserLoading, fetched } = useSelector(
    (state) => state.trello
  );

  // Fetch client data if needed
  useEffect(() => {
    if (email && !isClientFetched) {
      dispatch(fetchClientData());
    }
  }, [email, isClientFetched, dispatch]);

  // Fetch Trello user info if needed
  useEffect(() => {
    if (!fetched && !getUserLoading) {
      dispatch(fetchUserInfo());
    }
  }, [dispatch, email, getUserLoading, fetched]);

  const showReauthTutorial = !getUserLoading && email && fetched;

  // Scroll to the documentation section
  const handleScrollToDocs = () => {
    if (docRef.current) {
      docRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {showReauthTutorial ? (
        // =============== DOCUMENTATION LANDING PAGE ===============
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* HERO BANNER */}
          <Box>
            <Typography variant="h3">
              Board<span style={{ color: "#008c23", fontWeight: 900 }}>2</span>
              Export
            </Typography>
            <Divider sx={{ background: "black", mb: 1 }} />
            <Typography variant="h5" gutterBottom>
              Quick & Secure Data Exports for Trello
            </Typography>
            <Typography variant="subtitle1" fontWeight={300}>
              Export your Trello boards to Excel or CSV files in just a few
              clicks. Automate your daily or weekly exports and let Board2Export
              do the heavy lifting!
            </Typography>
          </Box>

          {/* DOCUMENTATION CONTAINER */}
          <Box
            ref={docRef}
            sx={{
              width: "100%",
              maxWidth: 1200,
              margin: "0 auto",
              padding: "2rem 1rem",
              backgroundColor: "#fafafa",
              borderRadius: 2,
              mb: 4,
            }}
          >
            {/* SECTION 1 - OVERVIEW */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h4" component="h2" gutterBottom>
                1. Overview
              </Typography>
              <Typography variant="body1" gutterBottom>
                Board2Export is a power-up developed by{" "}
                <strong>Ezy PowerUps</strong> that simplifies how you export
                your Trello data into Excel or CSV files. Whether you need quick
                backups, data analysis, or integration with other tools,
                Board2Export has you covered.
              </Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Key Features:</strong>
              </Typography>
              <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
                <li>
                  <strong>Unlimited Manual Exports:</strong> Export your board
                  data anytime with a single click.
                </li>
                <li>
                  <strong>Scheduled Exports:</strong> Automate daily or weekly
                  exports and receive reports via email.
                </li>
                <li>
                  <strong>Easy Email Integration:</strong> Send files directly
                  to any email address.
                </li>
                <li>
                  <strong>Security & Privacy:</strong> We strictly follow
                  Trello’s security standards, ensuring your data remains
                  protected.
                </li>
              </ul>
            </Paper>

            {/* SECTION 2 - MANUAL EXPORT */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h4" component="h2" gutterBottom>
                2. Manual Export
              </Typography>
              <Typography variant="body1" gutterBottom>
                You can perform manual exports whenever needed. Follow these
                steps:
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Access the Power-Up"
                    secondary="Within your Trello board, click on the Board2Export icon or find it in the side menu."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Select 'Export Now'"
                    secondary="Choose the desired format (Excel or CSV)."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Download the File"
                    secondary="Once generated, the download starts automatically or you’ll receive a link."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Done!"
                    secondary="Your spreadsheet is saved locally, ready for analysis or sharing."
                  />
                </ListItem>
              </List>
              <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                <strong>Tip:</strong> There’s no limit to the number of manual
                exports.
              </Typography>
              {/* SECTION 2 IMAGE */}
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <img
                  src="https://zealous-field-00cc03a10.5.azurestaticapps.net/img/img2.gif"
                  alt="Manual Export Tutorial"
                  style={{ maxWidth: "100%", borderRadius: 8 }}
                />
              </Box>
            </Paper>

            {/* SECTION 3 - SCHEDULING EXPORTS */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h4" component="h2" gutterBottom>
                3. Scheduling Exports
              </Typography>
              <Typography variant="body1" gutterBottom>
                Automate your workflow by scheduling exports to run daily or
                weekly. Here’s how:
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Open Board2Export"
                    secondary="Click the power-up icon on your board and go to 'Exportation' (or 'Exports')."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Go to 'Schedule'"
                    secondary="Select the 'Schedule' tab or section."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Add a New Schedule"
                    secondary="Click on 'Add new schedule'."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Configure Frequency"
                    secondary="Choose daily or weekly export intervals."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Set the Send Time"
                    secondary="Pick the best time to receive your reports."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Select File Format"
                    secondary="Excel (.xlsx) or CSV (.csv)."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Enter the Email"
                    secondary="Add the email address that should receive the exported file."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Save Schedule"
                    secondary="Click 'Save Schedule' to finalize."
                  />
                </ListItem>
              </List>
              <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                You can schedule up to the number of monthly exports included in
                your chosen plan, distributed across different days or boards,
                depending on your needs. The exports in your plan will be
                available for distribution across all boards where you are a
                user (using the same email associated with your plan).
              </Typography>
              {/* NEW TEXT IN ENGLISH */}
              <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                <strong>
                  The scheduled export may experience a few minutes of delay due
                  to processing and spreadsheet generation.
                </strong>
              </Typography>
              {/* SECTION 3 IMAGE */}
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <img
                  src="https://zealous-field-00cc03a10.5.azurestaticapps.net/img/img1.gif"
                  alt="Scheduling Exports Tutorial"
                  style={{ maxWidth: "100%", borderRadius: 8 }}
                />
              </Box>
            </Paper>

            {/* SECTION 4 - PLANS & 15-Day Free Trial */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h4" component="h2" gutterBottom>
                🎉 Plans & 15-Day Free Trial
              </Typography>
              <Typography variant="body1" gutterBottom>
                Start with our Free plan for 15 days to explore all the
                essential features at no cost. During this trial, you can
                schedule up to 3 exports per week. As long as you use the same
                email, you only need one subscription to apply Board2Export to
                multiple boards!
              </Typography>

              <Typography variant="body1" gutterBottom>
                When you’re ready for more flexibility, choose from one of our
                monthly plans:
              </Typography>

              <Typography variant="body1" gutterBottom>
                <strong>Basic I – $5/month</strong>
                <br />
                Up to 3 scheduled exports/week
              </Typography>

              <Typography variant="body1" gutterBottom>
                <strong>Basic II – $10/month</strong>
                <br />
                Up to 7 scheduled exports/week
              </Typography>

              <Typography variant="body1" gutterBottom>
                <strong>Intermediate I – $15/month</strong>
                <br />
                Up to 14 scheduled exports/week
              </Typography>

              <Typography variant="body1" gutterBottom>
                <strong>Intermediate II – $20/month</strong>
                <br />
                Up to 28 scheduled exports/week
              </Typography>

              <Typography variant="body1" gutterBottom>
                <strong>Premium I – $25/month</strong>
                <br />
                Up to 56 scheduled exports/week
              </Typography>

              <Typography variant="body1" gutterBottom>
                <strong>Premium II – $30/month</strong>
                <br />
                Up to 200 scheduled exports/week
              </Typography>

              <Typography variant="body1" gutterBottom>
                <strong>Ultra I – $50/month</strong>
                <br />
                Up to 600 scheduled exports/week
              </Typography>

              <Typography variant="body1" gutterBottom>
                <strong>Ultra II – $100/month</strong>
                <br />
                Up to 2000 scheduled exports/week
              </Typography>

              <Typography variant="body1" gutterBottom>
                Choose the plan that best fits your needs and keep your Trello
                data at your fingertips!
              </Typography>
            </Paper>

            {/* SECTION 5 - SECURITY & PRIVACY */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h4" component="h2" gutterBottom>
                5. Security & Privacy
              </Typography>
              <Typography variant="body1" gutterBottom>
                Ezy PowerUps takes data protection seriously. Board2Export
                strictly adheres to Trello’s security standards. Your data is
                processed securely, never shared with third parties, and remains
                your exclusive property at all times.
              </Typography>
              <Typography variant="body1">
                For more details, please review our Privacy Policy (if a
                specific link exists, you can include it here).
              </Typography>
            </Paper>

            {/* SECTION 6 - SUPPORT */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h4" component="h2" gutterBottom>
                6. Support
              </Typography>
              <Typography variant="body1" gutterBottom>
                Questions, issues, or suggestions? Contact our support team:
              </Typography>
              <Typography variant="body1" gutterBottom>
                Email: <strong>support.ezypowerups@smartezy.com.br</strong>
              </Typography>
              <Typography variant="body1" gutterBottom>
                We are always ready to help you get the most out of Board2Export!
              </Typography>
            </Paper>

            {/* SECTION 7 - CONCLUSION */}
            <Paper sx={{ p: 4 }}>
              <Typography variant="h4" component="h2" gutterBottom>
                Enjoy the Full Potential of Board2Export!
              </Typography>
              <Typography variant="body1">
                Now that you’re familiar with Board2Export, explore all the
                functionalities this power-up offers. Set up your automated
                reports, share information easily with your team, and keep
                everything organized in one place.
              </Typography>
            </Paper>
          </Box>
        </Box>
      ) : (
        // =============== REAUTHORIZATION TUTORIAL ===============
        <Box
          sx={{
            background: "linear-gradient(135deg, #EEEEEE 0%, #BDBDBD 100%)",
            minHeight: "100vh",
            p: 3,
          }}
        >
          <Box
            className={`${styles.header} w-100`}
            sx={{ mb: 2, textAlign: "center" }}
          >
            <Typography variant="h4" fontWeight="bold">
              How to reset Power-Up data to reauthorize
            </Typography>
          </Box>

          <Box sx={{ mt: 2, px: 2 }}>
            <Paper
              elevation={3}
              sx={{
                display: "flex",
                padding: 3,
                bgcolor: "#f5f5f5",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "start",
                  overflow: "hidden",
                  maxWidth: "500px",
                  margin: "0 auto",
                }}
              >
                <img
                  src={require("../../assets/img/tuto_img.gif")}
                  alt="Instructions Example"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "cover",
                    objectPosition: "top",
                    borderRadius: 8,
                  }}
                />
              </Box>
              <Box sx={{ flex: 2, ml: { xs: 0, md: 3 }, mt: { xs: 3, md: 0 } }}>
                <Typography fontWeight={300} variant="body1" sx={{ mb: 2 }}>
                  To use the Power-Up, you must first grant the necessary
                  authorization. If you accidentally denied the authorization,
                  follow the steps below to reset the Power-Up data and try
                  again:
                </Typography>
                <List sx={{ paddingLeft: 2 }}>
                  <ListItem
                    sx={{
                      transition: "background-color 0.2s",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                        backgroundColor: "#1976d2",
                        color: "white",
                        mr: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        1
                      </Typography>
                    </Box>
                    <ListItemText
                      primary="Go to your Board"
                      secondary="Click on the 'Power-Ups' button in the top-right corner of your Trello board."
                    />
                  </ListItem>

                  <ListItem
                    sx={{
                      transition: "background-color 0.2s",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                        backgroundColor: "#1976d2",
                        color: "white",
                        mr: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        2
                      </Typography>
                    </Box>
                    <ListItemText
                      primary="Click the Settings Button"
                      secondary="Once the Power-Up is located, click on its 'Settings' button."
                    />
                  </ListItem>

                  <ListItem
                    sx={{
                      transition: "background-color 0.2s",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                        backgroundColor: "#1976d2",
                        color: "white",
                        mr: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        3
                      </Typography>
                    </Box>
                    <ListItemText
                      primary="Click on 'Remove Personal Settings'"
                      secondary="In the settings menu, find and click 'Remove Personal Settings'."
                    />
                  </ListItem>

                  <ListItem
                    sx={{
                      transition: "background-color 0.2s",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                        backgroundColor: "#1976d2",
                        color: "white",
                        mr: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        4
                      </Typography>
                    </Box>
                    <ListItemText
                      primary="Confirm Data Reset"
                      secondary="Confirm the action to clear the Power-Up's data."
                    />
                  </ListItem>

                  <ListItem
                    sx={{
                      transition: "background-color 0.2s",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                        backgroundColor: "#1976d2",
                        color: "white",
                        mr: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        5
                      </Typography>
                    </Box>
                    <ListItemText
                      primary="Refresh the Page"
                      secondary="Reload your Trello board to apply the changes and reauthorize the Power-Up."
                    />
                  </ListItem>
                </List>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}
    </div>
  );
}

export default InstructionsPage;
